import React, { useEffect } from 'react'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { Market } from '@s4l/stayforlong/shared/domain/Market'
import { Language } from '@s4l/stayforlong/shared/domain/Language'
import debug from 'debug'
import styles from './index.module.css'

const log = debug('s4l:uikit:AdBanner')

interface Props {
  readonly adUnit: string
  readonly id: string
}

// Type declaration for googletag
declare global {
  interface Window {
    googletag: {
      cmd: Array<() => void>
      defineSlot: (adUnitPath: string, size: number[][] | number[], div: string) => any
      display: (id: string) => void
      pubads: () => any
      sizeMapping: () => any
      destroySlots: (slots: any[]) => void
    }
  }
}

const networkCode = process.env.NEXT_PUBLIC_GAM_NETWORK_CODE

const getGoogletag = () => {
  if (typeof globalThis.window === 'undefined') return null
  return globalThis.window.googletag ?? null
}

const destroySlot = (googletag: Window['googletag'], slotId: string) => {
  const slots = googletag.pubads().getSlots()
  const currentSlot = slots.find((s: any) => s.getSlotElementId() === slotId)
  if (currentSlot) {
    googletag.destroySlots([currentSlot])
  }
}

export default function AdBanner({ adUnit, id }: Props) {
  const {
    data: market = Market.default(),
    loading: isLoadingMarket,
    error: marketError,
  } = useUseCase('GetCurrentMarketSharedUseCase')

  if (marketError) {
    log('Error in GetCurrentMarketSharedUseCase')
  }

  const {
    data: language = Language.default(),
    loading: isLoadingLanguage,
    error: languageError,
  } = useUseCase('GetCurrentLanguageSharedUseCase')

  if (languageError) {
    log('Error in GetCurrentLanguageSharedUseCase')
  }

  const isLoading = isLoadingMarket || isLoadingLanguage

  useEffect(() => {
    if (!networkCode || isLoading) {
      return undefined
    }

    const googletag = getGoogletag()
    if (!googletag) {
      log('Google Tag Manager not loaded')
      return undefined
    }

    googletag.cmd.push(() => {
      const mapping = googletag
        .sizeMapping()
        .addSize([1024, 0], [728, 90])
        .addSize([768, 0], [728, 90])
        .addSize([0, 0], [300, 250])
        .build()

      const slot = googletag
        .defineSlot(
          adUnit,
          [
            [728, 90],
            [300, 250],
          ],
          id
        )
        ?.defineSizeMapping(mapping)
        ?.addService(googletag.pubads())

      if (slot) {
        slot.setTargeting('market', market.value)
        slot.setTargeting('language', language.value)

        googletag.display(id)
        googletag.pubads().refresh([slot])
      }
    })

    return () => {
      const gTag = getGoogletag()
      if (gTag) {
        gTag.cmd.push(() => destroySlot(gTag, id))
      }
    }
  }, [adUnit, id, isLoading, market, language])

  if (!networkCode) {
    return null
  }

  return (
    <div className={styles.container}>
      <div id={id} className={styles.adSlot} />
    </div>
  )
}
AdBanner.displayName = 'AdBanner'
